import { useListingField, useListingStore } from '@brightmls/nextgen.lae.flc';
import { ListingResource, PropertyField } from '../../../system/field';
import {
  formatAddressLineOne,
  formatAddressLineTwo,
} from '../../../system/location';

// ---------------------------------------------------------------------------
// Field Config & Section Hook
// ---------------------------------------------------------------------------

type FieldDef = {
  key: PropertyField;
  label: string;
  format?: 'price';
  group?: string;
};
export type FieldEntry = {
  label: string;
  value: any;
  format?: 'price';
  group?: string;
};

export const PREVIEW_FIELDS_CONFIG: Record<string, FieldDef[]> = {
  internetSettings: [
    {
      key: PropertyField.INTERNET_ENTIRE_LISTING_DISPLAY_YN,
      label: 'Seller Authorizes the listing on the Internet',
    },
    {
      key: PropertyField.INTERNET_ADDRESS_DISPLAY_YN,
      label: 'Seller Authorizes Address on the Internet',
    },
    {
      key: PropertyField.INTERNET_AUTOMATED_VALUATION_DISPLAY_YN,
      label: 'Seller Authorizes AVM',
    },
    {
      key: PropertyField.INTERNET_CONSUMER_COMMENT_YN,
      label: 'Seller Authorizes Consumer Comments',
    },
  ],
  listingDetails: [
    { key: PropertyField.PROPERTY_TYPE, label: 'Property Type' },
    { key: PropertyField.LISTING_ID, label: 'MLS Number' },
    { key: PropertyField.COMPARABLE_YN, label: 'Comparable Y/N' },
    { key: PropertyField.OFFICE_EXCLUSIVE_YN, label: 'Office Exclusive Y/N' },
    { key: PropertyField.MLS_STATUS, label: 'Status' },
    { key: PropertyField.LIST_PRICE, label: 'List Price', format: 'price' },
    { key: PropertyField.LISTING_AGREEMENT, label: 'Listing Agreement Type' },
    { key: PropertyField.LISTING_TERMS, label: 'Listing Terms' },
    { key: PropertyField.EXPIRATION_DATE, label: 'Expiration Date' },
    { key: PropertyField.POSSESSION, label: 'Possession' },
    { key: PropertyField.LISTING_SERVICE, label: 'Listing Service Type' },
  ],
  ownershipAgents: [
    { key: PropertyField.SALE_TYPE, label: 'Sale Type' },
    { key: PropertyField.SHORT_SALE, label: 'Short Sale' },
    { key: PropertyField.DUAL_AGENCY_YN, label: 'Dual Agency Y/N' },
    {
      key: PropertyField.PROSPECTS_EXCLUDED_YN,
      label: 'Prospects Excluded Y/N',
    },
    { key: PropertyField.LIST_AGENT_FULL_NAME, label: 'List Agent' },
    { key: PropertyField.LIST_AGENT_MLS_ID, label: 'List Agent MLS ID' },
    { key: PropertyField.LIST_OFFICE_NAME, label: 'List Office' },
    { key: PropertyField.OWNER_1_NAME, label: 'Owner 1 Name' },
    { key: PropertyField.OWNER_1_EMAIL, label: 'Owner 1 Email' },
    { key: PropertyField.OWNER_1_MOBILE_PHONE, label: 'Owner 1 Mobile' },
    { key: PropertyField.OWNER_2_NAME, label: 'Owner 2 Name' },
    { key: PropertyField.OWNER_2_EMAIL, label: 'Owner 2 Email' },
    { key: PropertyField.OWNER_2_MOBILE_PHONE, label: 'Owner 2 Mobile' },
  ],
  association: [
    { key: PropertyField.ASSOCIATION_YN, label: 'HOA Y/N' },
    {
      key: PropertyField.ASSOCIATION_FEE,
      label: 'Association Fee',
      format: 'price',
    },
    {
      key: PropertyField.ASSOCIATION_FEE_FREQUENCY,
      label: 'Association Fee Frequency',
    },
    {
      key: PropertyField.ASSOCIATION_FEE_INCLUDES,
      label: 'HOA/Condo/Coop Fee Includes',
    },
    { key: PropertyField.SENIOR_COMMUNITY_YN, label: 'Senior Community Y/N' },
  ],
  taxInfo: [
    { key: PropertyField.TAX_ID, label: 'Tax ID Number' },
    {
      key: PropertyField.AUTO_UPDATE_TAX_DATA_YN,
      label: 'Automatically Update Tax Values Y/N',
    },
    { key: PropertyField.ZONING, label: 'Zoning' },
    { key: PropertyField.COUNTY_TAX, label: 'County Tax', format: 'price' },
    { key: PropertyField.TAX_YEAR, label: 'Tax Year' },
    {
      key: PropertyField.TAX_ANNUAL_AMOUNT,
      label: 'Tax Annual Amount',
      format: 'price',
    },
  ],
  propertyDetails: [
    { key: PropertyField.NEW_CONSTRUCTION_YN, label: 'New Construction Y/N' },
    { key: PropertyField.YEAR_BUILT, label: 'Year Built' },
    { key: PropertyField.ARCHITECTURAL_STYLE, label: 'Style' },
    {
      key: PropertyField.CONSTRUCTION_MATERIALS,
      label: 'Construction Materials',
    },
    { key: PropertyField.LEVELS, label: 'Levels/Stories' },
    { key: PropertyField.WATER_SOURCE, label: 'Water Source' },
    { key: PropertyField.SEWER, label: 'Sewer Septic' },
    { key: PropertyField.HOT_WATER, label: 'Hot Water' },
    { key: PropertyField.COOLING, label: 'Cooling Type' },
    { key: PropertyField.COOLING_FUEL, label: 'Cooling Fuel' },
    { key: PropertyField.HEATING, label: 'Heating Type' },
    { key: PropertyField.HEATING_FUEL, label: 'Heating Fuel' },
    { key: PropertyField.BASEMENT_YN, label: 'Basement Y/N' },
    { key: PropertyField.BASEMENT, label: 'Basement' },
    { key: PropertyField.FOUNDATION_DETAILS, label: 'Foundation Details' },
  ],
  sizeRooms: [
    {
      key: PropertyField.ABOVE_GRADE_FINISHED_AREA,
      label: 'Above Grade Finished SQFT',
    },
    { key: PropertyField.LIVING_AREA, label: 'Total Finished SQFT' },
    { key: PropertyField.BEDROOMS_TOTAL, label: 'Bedrooms Total' },
    { key: PropertyField.BATHROOMS_FULL, label: 'Full Bathrooms' },
    { key: PropertyField.BATHROOMS_HALF, label: 'Half Bathrooms' },
  ],
  features: [
    {
      key: PropertyField.ACCESSIBILITY_FEATURES,
      label: 'Accessibility Features',
    },
    { key: PropertyField.APPLIANCES, label: 'Appliances' },
    { key: PropertyField.POOL_PRIVATE_YN, label: 'Pool' },
    { key: PropertyField.POOL_FEATURES, label: 'Pool Features' },
    { key: PropertyField.PARKING_FEATURES, label: 'Type Of Parking' },
    { key: PropertyField.PARKING_TOTAL, label: 'Total Parking Spaces' },
    { key: PropertyField.WATER_ORIENTED_YN, label: 'Water Oriented Y/N' },
  ],
  showingInfo: [
    { key: PropertyField.SHOWING, label: 'Showing Requirements' },
    { key: PropertyField.SHOWING_INSTRUCTIONS, label: 'Showing Instructions' },
    { key: PropertyField.LOCK_BOX_TYPE, label: 'Lock Box Type' },
  ],
};

const getFields = (defs: FieldDef[], property: Record<string, any>) =>
  defs
    .map((d) => ({
      label: d.label,
      value: String(property[d.key] ?? ''),
      format: d.format,
      group: d.group,
    }))
    .filter(
      (f) => f.value !== '' && f.value !== 'undefined' && f.value !== 'null',
    );

/**
 * Read all listing field sections at once and return only those with non-empty values.
 */
export function usePreviewFields(): Record<string, FieldEntry[]> {
  return useListingStore((state) => {
    const property = state.Property ?? {};

    const fields: Record<string, FieldEntry[]> = {};
    Object.keys(PREVIEW_FIELDS_CONFIG).forEach((key) => {
      fields[key] = getFields(PREVIEW_FIELDS_CONFIG[key], property);
    });

    return fields;
  });
}

// ---------------------------------------------------------------------------
// Header / highlights hooks
// ---------------------------------------------------------------------------

/** Modification timestamp for "Last saved on" */
export function usePreviewHeader() {
  const modTimestamp = useListingField(
    ListingResource.Property,
    PropertyField.MODIFICATION_TIMESTAMP,
    '',
  ).value;

  return { modificationTimestamp: modTimestamp };
}

/** Agent contact info for the orange bar */
export function usePreviewAgent() {
  const agentName = useListingField(
    ListingResource.Property,
    PropertyField.LIST_AGENT_FULL_NAME,
    '',
  ).value;
  const officeName = useListingField(
    ListingResource.Property,
    PropertyField.LIST_OFFICE_NAME,
    '',
  ).value;
  const agentPhone = useListingField(
    ListingResource.Property,
    PropertyField.LIST_AGENT_DIRECT_PHONE,
    '',
  ).value;

  return { agentName, officeName, agentPhone };
}

const useHighlightField = (field: string) =>
  useListingField(ListingResource.Property, field, '').value;

/** Core highlights: address, price, beds, baths, sqft, status, type, MLS# */
export function usePreviewHighlights() {
  const streetNumber = useHighlightField(PropertyField.STREET_NUMBER);
  const streetDirPrefix = useHighlightField(PropertyField.STREET_DIR_PREFIX);
  const streetName = useHighlightField(PropertyField.STREET_NAME);
  const streetSuffix = useHighlightField(PropertyField.STREET_SUFFIX);
  const streetDirSuffix = useHighlightField(PropertyField.STREET_DIR_SUFFIX);
  const unitNumber = useHighlightField(PropertyField.UNIT_NUMBER);
  const city = useHighlightField(PropertyField.CITY);
  const state = useHighlightField(PropertyField.STATE_OR_PROVINCE);
  const zipCode = useHighlightField(PropertyField.POSTAL_CODE);
  const zipCodePlus4 = useHighlightField(PropertyField.POSTAL_CODE_PLUS4);

  const listPrice = useHighlightField(PropertyField.LIST_PRICE);
  const beds = useHighlightField(PropertyField.BEDROOMS_TOTAL);
  const baths = useHighlightField(PropertyField.BATHROOMS_FULL);
  const halfBaths = useHighlightField(PropertyField.BATHROOMS_HALF);
  const sqft = useHighlightField(PropertyField.LIVING_AREA);
  const aboveGrade = useHighlightField(PropertyField.ABOVE_GRADE_FINISHED_AREA);
  const propertyType = useHighlightField(PropertyField.PROPERTY_TYPE);
  const mlsStatus = useHighlightField(PropertyField.MLS_STATUS);
  const listingId = useHighlightField(PropertyField.LISTING_ID);
  const lotSizeAcres = useHighlightField(PropertyField.LOT_SIZE_ACRES);
  const lotSizeSqft = useHighlightField(PropertyField.LOT_SIZE_SQUARE_FEET);

  const addressLine1 = formatAddressLineOne({
    streetNumber,
    streetDirPrefix,
    streetName,
    streetSuffix,
    streetDirSuffix,
    unitNumber,
  });

  const addressLine2 = formatAddressLineTwo({
    city,
    state,
    zipCode,
    zipCodePlus4,
  });

  return {
    addressLine1,
    addressLine2,
    listPrice,
    beds,
    baths,
    halfBaths,
    sqft,
    aboveGrade,
    propertyType,
    mlsStatus,
    listingId,
    lotSizeAcres,
    lotSizeSqft,
  };
}

/** Lat/lng for the HERE map */
export function usePreviewMap() {
  const lat = useListingField(
    ListingResource.Property,
    PropertyField.LATITUDE,
    '',
  ).value;
  const lng = useListingField(
    ListingResource.Property,
    PropertyField.LONGITUDE,
    '',
  ).value;

  return {
    latitude: parseFloat(lat) || 0,
    longitude: parseFloat(lng) || 0,
    hasCoordinates: !!lat && !!lng,
  };
}

// ---------------------------------------------------------------------------
// Public remarks (standalone)
// ---------------------------------------------------------------------------

export function usePreviewRemarks() {
  return useListingField(
    ListingResource.Property,
    PropertyField.PUBLIC_REMARKS,
    '',
  ).value;
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

/** Read media items from the FLC listing store */
export function usePreviewMedia() {
  const media: any[] = useListingStore((state) => state.Media ?? []);

  return media.filter(
    (m: any) => m.MEDIA_CATEGORY === 'Photo' || m.MED_CATEGORY === 'Photo',
  );
}
